import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Callout,
    Checkbox,
    ComboBox,
    DefaultButton,
    DirectionalHint,
    Dropdown,
    IconButton,
    IDropdownOption,
    Label,
    MessageBar,
    MessageBarType,
    Modal,
    PrimaryButton,
    SpinButton,
    TextField
} from "@fluentui/react";
import {Editor, wordCountPlugin} from "lilac-editor";
import {IAdditionalData, IFieldDescriptor} from "../../models/Extension";
import {useLazyGetExtensionFieldMetadataQuery} from "../../store/Api";
import TokenAutosuggestField from "../Common/TokenAutosuggestField";
import TokenSuggestionList from "../Common/TokenSuggestionList";
import {ITokenSuggestion} from "../Storefront/useOpportunityTokens";

// One saved entry in the field-map blob. `value` is always a string (the server coerces by `dataType`).
interface IFieldMapEntry {
    value: string;
    dataType: string;
}

type FieldMap = { [key: string]: IFieldMapEntry };

interface IExtensionDynamicFieldMapProps {
    field: IAdditionalData;
    value: string;                 // JSON blob of selected field mappings
    parentFieldValue?: string;     // value of the parent selector (e.g. selected ticket type), if any
    extensionId: string;
    onChange: (value: string) => void;
    onLoading?: (loading: boolean) => void;
    updateOnDisengage?: boolean;
    // When set, text-type mapped values offer these as inline token autosuggest (the whole map blob is tokenized
    // server-side when the parent field supports tokens). Empty/undefined leaves the inputs as plain text.
    tokenSuggestions?: ITokenSuggestion[];
}

const parseMap = (raw: string): { map: FieldMap; error: boolean } => {
    if (!raw) return {map: {}, error: false};
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return {map: parsed as FieldMap, error: false};
        return {map: {}, error: true};
    } catch {
        return {map: {}, error: true};
    }
};

/**
 * Renders a form-driven set of typed inputs for a dynamicFieldMap field. Fetches the available fields for the
 * selected form (via the extension's fieldMetadata endpoint), lets the user pick which to send a value to, and
 * serialises the result to a single JSON blob. Clearing on form change is handled by the parent form (it zeroes
 * this field's value when the parent selector changes); this control refetches and resets when that happens.
 */
const ExtensionDynamicFieldMap: React.FC<IExtensionDynamicFieldMapProps> = (props) => {
    const initial = useMemo(() => parseMap(props.value), [props.value]);
    const [map, setMap] = useState<FieldMap>(initial.map);
    const [parseError, setParseError] = useState<boolean>(initial.error);
    const [descriptors, setDescriptors] = useState<IFieldDescriptor[] | undefined>(undefined);
    const [fetchError, setFetchError] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [metadataApi] = useLazyGetExtensionFieldMetadataQuery();
    const [addOpen, setAddOpen] = useState<boolean>(false);

    /*
     * Its own prefix rather than reusing extension.<src>.<field>: the mapped key sits *under* a
     * parent additional-data field, and folding both into one dotted tail would make the two shapes
     * indistinguishable. Folio ids are skipped for the same reason they are in ExtensionDataForm —
     * that id shape isn't settled yet.
     */
    const settingIdFor = (mappedKey: string): string | undefined =>
        !props.extensionId || props.extensionId.startsWith('folio_')
            ? undefined
            : `extensionFieldMap.${props.extensionId}.${props.field.field}.${mappedKey}`;
    const [addKey, setAddKey] = useState<string | undefined>(undefined);
    const [addValue, setAddValue] = useState<string>("");
    const addAnchorRef = useRef<HTMLSpanElement>(null);
    // Rich text is edited in a shared full-screen modal (rather than cramped inline). While it's open we keep a
    // reference to the value + its commit callback, and hold the add popover open (preventDismissOnEvent).
    const [richEditor, setRichEditor] = useState<{value: string; onSave: (v: string) => void} | null>(null);

    const hasParent = !!props.field.parentField;
    const gatedOnParent = hasParent && !props.parentFieldValue;

    // Keep local state in sync when the stored value changes (e.g. parent-driven clear sets it to '').
    useEffect(() => {
        const {map: parsed, error} = parseMap(props.value);
        setMap(parsed);
        setParseError(error);
    }, [props.value]);

    // Fetch field metadata for the selected form. Gated until the parent selector has a value (when there is one).
    useEffect(() => {
        if (gatedOnParent || !props.extensionId) {
            setDescriptors(undefined);
            return;
        }
        const additionalData = hasParent && props.field.parentField
            ? {[props.field.parentField]: props.parentFieldValue ?? ""}
            : undefined;
        setLoading(true);
        setFetchError(undefined);
        props.onLoading?.(true);
        metadataApi({
            extensionId: props.extensionId,
            field: props.field.field,
            additionalData
        }).then((res) => {
            if (res.data?.success) {
                setDescriptors(res.data.fields ?? []);
            } else {
                setFetchError(res.data?.error ?? "Failed to load fields. Please verify your credentials and permissions.");
                setDescriptors([]);
            }
        }).catch(() => {
            setFetchError("Failed to load fields. Please verify your credentials and permissions.");
            setDescriptors([]);
        }).finally(() => {
            setLoading(false);
            props.onLoading?.(false);
        });
    }, [props.extensionId, props.field.field, props.parentFieldValue, gatedOnParent]);

    const commit = (next: FieldMap) => {
        setMap(next);
        props.onChange(JSON.stringify(next));
    };

    const setValue = (descriptor: IFieldDescriptor, value: string) => {
        commit({...map, [descriptor.key]: {value, dataType: descriptor.dataType}});
    };

    const removeMapping = (key: string) => {
        const next = {...map};
        delete next[key];
        commit(next);
    };

    // Fields available on the form that haven't been mapped yet — the candidates offered by the add control.
    const availableToAdd = useMemo(
        () => (descriptors ?? []).filter(d => !(d.key in map)),
        [descriptors, map]
    );
    const addDescriptor = descriptors?.find(d => d.key === addKey);

    const openAdd = () => {
        setAddKey(undefined);
        setAddValue("");
        setAddOpen(true);
    };

    const confirmAdd = () => {
        if (!addDescriptor) return;
        commit({...map, [addDescriptor.key]: {value: addValue, dataType: addDescriptor.dataType}});
        setAddOpen(false);
        setAddKey(undefined);
        setAddValue("");
    };

    if (gatedOnParent) {
        return (
            <div style={{paddingTop: "10px"}}>
                <Label>{props.field.description}</Label>
                <MessageBar messageBarType={MessageBarType.info}>
                    Select {props.field.parentField ? "the form" : "a form"} first to choose fields.
                </MessageBar>
            </div>
        );
    }

    const mappedKeys = Object.keys(map);

    return (
        <div style={{paddingTop: "10px", paddingBottom: "10px"}}>
            <Label>{props.field.description}</Label>
            {props.field.toolTip && <p style={{marginTop: 0, color: "#605e5c"}}>{props.field.toolTip}</p>}

            {parseError &&
                <MessageBar messageBarType={MessageBarType.warning}>
                    The saved field mapping could not be read and was reset.
                </MessageBar>}
            {fetchError &&
                <MessageBar messageBarType={MessageBarType.error}>{fetchError}</MessageBar>}

            {loading && <p>Loading fields…</p>}

            {!loading && descriptors && <>
                {descriptors.length === 0
                    ? <p style={{color: "#605e5c"}}>No fields are available for this form.</p>
                    : mappedKeys.length === 0 &&
                        <p style={{color: "#605e5c"}}>No fields mapped yet. Use the + button below to add one.</p>}

                {mappedKeys.map((key) => {
                    const descriptor = descriptors.find(d => d.key === key);
                    const entry = map[key];
                    const label = descriptor?.label ?? key;
                    // Marked on the row rather than the control: TypedInput renders one of ten
                    // controls depending on the descriptor, and the row covers all of them with one
                    // id. Which fields exist is decided by the extension's own schema, so the id is
                    // dynamic — a support link can name one if they know it is there.
                    return (
                        <div key={key} data-setting-id={settingIdFor(key)} style={{display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", borderLeft: "3px solid #edebe9", paddingLeft: "10px"}}>
                            <div style={{flexGrow: 1}}>
                                <Label>{label}{descriptor?.required ? " *" : ""}{descriptor?.isCustom ? " (custom)" : ""}</Label>
                                {descriptor
                                    ? <>
                                        <TypedInput
                                            descriptor={descriptor}
                                            value={entry.value}
                                            updateOnDisengage={props.updateOnDisengage}
                                            onChange={(v) => setValue(descriptor, v)}
                                            openRichEditor={(v, onSave) => setRichEditor({value: v, onSave})}
                                            tokenSuggestions={props.tokenSuggestions}
                                        />
                                        {descriptor.required && !entry.value &&
                                            <p className={"field-error-message"}>Value required for {label}</p>}
                                    </>
                                    : <MessageBar messageBarType={MessageBarType.warning}>
                                        This field is no longer available on the form and will not be sent.
                                    </MessageBar>}
                            </div>
                            <IconButton
                                iconProps={{iconName: "Delete"}}
                                title={`Remove ${label}`}
                                ariaLabel={`Remove ${label}`}
                                onClick={() => removeMapping(key)}
                            />
                        </div>
                    );
                })}

                <span ref={addAnchorRef} style={{display: "inline-block"}}>
                    <IconButton
                        iconProps={{iconName: "Add"}}
                        title="Add field mapping"
                        ariaLabel="Add field mapping"
                        disabled={availableToAdd.length === 0}
                        onClick={openAdd}
                    />
                </span>
                {availableToAdd.length === 0 && descriptors.length > 0 &&
                    <span style={{color: "#605e5c", marginLeft: "6px"}}>All available fields are mapped.</span>}

                {addOpen &&
                    <Callout
                        target={addAnchorRef}
                        onDismiss={() => setAddOpen(false)}
                        directionalHint={DirectionalHint.bottomLeftEdge}
                        preventDismissOnEvent={() => richEditor !== null}
                        setInitialFocus>
                        <div style={{padding: "16px", minWidth: "280px", display: "flex", flexDirection: "column", gap: "10px"}}>
                            <Dropdown
                                label="Field"
                                placeholder="Select a field"
                                options={availableToAdd.map(d => ({key: d.key, text: `${d.label}${d.isCustom ? " (custom)" : ""}`}))}
                                selectedKey={addKey}
                                onChange={(_e, option) => { setAddKey(option?.key?.toString()); setAddValue(""); }}
                            />
                            {addDescriptor &&
                                <div>
                                    <Label>Value{addDescriptor.required ? " *" : ""}</Label>
                                    <TypedInput
                                        key={addDescriptor.key}
                                        descriptor={addDescriptor}
                                        value={addValue}
                                        onChange={setAddValue}
                                        openRichEditor={(v, onSave) => setRichEditor({value: v, onSave})}
                                        tokenSuggestions={props.tokenSuggestions}
                                    />
                                </div>}
                            <div style={{display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px"}}>
                                <DefaultButton text="Cancel" onClick={() => setAddOpen(false)} />
                                <PrimaryButton text="Add" onClick={confirmAdd} disabled={!addDescriptor} />
                            </div>
                        </div>
                    </Callout>}
            </>}

            {richEditor &&
                <RichTextEditorModal
                    initialValue={richEditor.value}
                    onApply={(v) => { richEditor.onSave(v); setRichEditor(null); }}
                    onDismiss={() => setRichEditor(null)}
                    tokenSuggestions={props.tokenSuggestions}
                />}
        </div>
    );
};

interface ITypedInputProps {
    descriptor: IFieldDescriptor;
    value: string;
    onChange: (value: string) => void;
    updateOnDisengage?: boolean;
    // Opens the shared full-screen rich text editor for this field's value.
    openRichEditor?: (value: string, onSave: (v: string) => void) => void;
    // When set, free-text inputs offer inline token autosuggest.
    tokenSuggestions?: ITokenSuggestion[];
}

/** Renders a single input whose control varies by the descriptor's data type. Value is always a string. */
const TypedInput: React.FC<ITypedInputProps> = ({descriptor, value, onChange, updateOnDisengage, openRichEditor, tokenSuggestions}) => {
    const [local, setLocal] = useState<string>(value);
    useEffect(() => { if (local !== value) setLocal(value); }, [value]);
    const tokensEnabled = (tokenSuggestions?.length ?? 0) > 0;

    const commitLocal = (v: string) => {
        setLocal(v);
        if (!updateOnDisengage) onChange(v);
    };

    const options: IDropdownOption[] = (descriptor.options ?? []).map(o => ({key: o.value, text: o.display}));

    switch (descriptor.dataType) {
        case "boolean":
            return <Checkbox
                checked={value === "true"}
                onChange={(_e, checked) => onChange(checked ? "true" : "false")}
            />;
        case "integer":
            return <SpinButton
                value={local}
                step={1}
                onChange={(_e, v) => { if (v !== undefined) commitLocal(v); }}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }}
            />;
        case "decimal":
        case "money":
            return <TextField
                type="number"
                value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }}
            />;
        case "date":
            return <TextField type="date" value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }} />;
        case "datetime":
            return <TextField type="datetime-local" value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }} />;
        case "time":
            return <TextField type="time" value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }} />;
        case "singleSelect":
            return <Dropdown
                options={options}
                selectedKey={value || undefined}
                onChange={(_e, option) => onChange(option?.key?.toString() ?? "")}
            />;
        case "multiSelect":
            return <ComboBox
                multiSelect
                options={options}
                // Stored as a comma-separated list of option values.
                selectedKey={value ? value.split(",") : []}
                onChange={(_e, option) => {
                    if (!option) return;
                    const current = value ? value.split(",").filter(Boolean) : [];
                    const key = option.key.toString();
                    const next = option.selected ? [...new Set([...current, key])] : current.filter(k => k !== key);
                    onChange(next.join(","));
                }}
            />;
        case "richText": {
            // Rich text is edited in a full-screen modal, not inline (it's cramped in the field row / add popover).
            // Show a placeholder button that opens the editor, with a short plain-text preview of any content.
            const preview = value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
            return <div>
                <DefaultButton
                    iconProps={{iconName: "PageEdit"}}
                    text={preview ? "Edit rich text…" : "Click to add rich text…"}
                    onClick={() => openRichEditor?.(value, onChange)}
                    styles={{root: {width: "100%", justifyContent: "flex-start"}}}
                />
                {preview &&
                    <div style={{marginTop: 4, color: "#605e5c", fontSize: 12, maxHeight: 40, overflow: "hidden"}}>
                        {preview.slice(0, 140)}{preview.length > 140 ? "…" : ""}
                    </div>}
            </div>;
        }
        case "multiLineText":
            if (tokensEnabled) {
                return <TokenAutosuggestField multiline rows={4} value={value} onChange={onChange}
                    updateOnDisengage={updateOnDisengage} suggestions={tokenSuggestions ?? []} />;
            }
            return <TextField multiline rows={4} value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }} />;
        case "string":
        case "text":
        default:
            if (tokensEnabled) {
                return <TokenAutosuggestField value={value} onChange={onChange}
                    updateOnDisengage={updateOnDisengage} suggestions={tokenSuggestions ?? []} />;
            }
            return <TextField value={local}
                onChange={(_e, v) => commitLocal(v ?? "")}
                onBlur={(e) => { if (updateOnDisengage) onChange(e.target.value); }} />;
    }
};

interface IRichTextEditorModalProps {
    initialValue: string;
    onApply: (value: string) => void;
    onDismiss: () => void;
    tokenSuggestions?: ITokenSuggestion[];
}

/**
 * Full-screen modal wrapper around the app's rich text editor (lilac-editor). Rich text fields open this so they
 * get a large editing surface instead of the cramped field row / add popover. Edits are buffered and committed on
 * Apply. Rendered in its own layer (Fluent Modal), above the add popover, which is held open while it's active.
 * When token suggestions are supplied, RichTextTokenSuggest offers the same {{{token}}} autosuggest inside the
 * editor as the plain fields do.
 */
const RichTextEditorModal: React.FC<IRichTextEditorModalProps> = ({initialValue, onApply, onDismiss, tokenSuggestions}) => {
    const [draft, setDraft] = useState<string>(initialValue);
    const editorContainerRef = useRef<HTMLDivElement>(null);
    return (
        <Modal isOpen isBlocking onDismiss={onDismiss}
            styles={{main: {width: "80vw", height: "65vh", maxWidth: "1200px", display: "flex", flexDirection: "column"}}}>
            <div style={{display: "flex", flexDirection: "column", height: "100%", padding: "16px", boxSizing: "border-box"}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px"}}>
                    <h3 style={{margin: 0}}>Edit rich text</h3>
                    <IconButton iconProps={{iconName: "Cancel"}} ariaLabel="Close editor" onClick={onDismiss} />
                </div>
                <div ref={editorContainerRef} style={{flexGrow: 1, overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 4}}>
                    <Editor
                        initialContent={initialValue}
                        onChange={setDraft}
                        plugins={[wordCountPlugin]}
                        toolbar={{
                            show: true,
                            tools: ['bold', 'italic', 'underline', 'heading1', 'heading2', 'heading3', 'bulletList', 'orderedList', 'link', 'image'],
                        }}
                    />
                </div>
                {(tokenSuggestions?.length ?? 0) > 0 &&
                    <RichTextTokenSuggest containerRef={editorContainerRef} suggestions={tokenSuggestions ?? []} />}
                <div style={{display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "12px"}}>
                    <DefaultButton text="Cancel" onClick={onDismiss} />
                    <PrimaryButton text="Apply" onClick={() => onApply(draft)} />
                </div>
            </div>
        </Modal>
    );
};

interface IRichTextTokenSuggestProps {
    containerRef: React.RefObject<HTMLElement>;
    suggestions: ITokenSuggestion[];
}

// 1–3 opening braces then a partial token identifier (word chars, ':' and '.' as used by quote_request tokens).
const RICH_TRIGGER = /\{{1,3}([\w.:]*)$/;
const RICH_MAX_SUGGESTIONS = 50;

/**
 * Token autosuggest for the contenteditable rich-text editor. lilac-editor is uncontrolled (seeded via
 * initialContent) and edits the live DOM, so we drive suggestions off the native Selection/Range APIs rather than
 * any editor-specific hook: watch the caret via delegated listeners on the editor container, and on pick replace
 * the typed trigger with the full {{{token}}} using execCommand (falling back to a Range insert). The callout is
 * anchored to the caret's screen position so it tracks as the user types.
 */
const RichTextTokenSuggest: React.FC<IRichTextTokenSuggestProps> = ({containerRef, suggestions}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [highlight, setHighlight] = useState(0);
    const [point, setPoint] = useState<{x: number; y: number}>({x: 0, y: 0});
    const triggerLen = useRef(0);

    const matches = useMemo<ITokenSuggestion[]>(() => {
        if (!open) return [];
        const q = query.toLowerCase();
        const stripped = (t: string) => t.replace(/[{}]/g, "").toLowerCase();
        return suggestions
            .filter(s => q === "" || stripped(s.token).includes(q) || s.description.toLowerCase().includes(q))
            .slice(0, RICH_MAX_SUGGESTIONS);
    }, [open, query, suggestions]);

    // Mirror state into refs so the (stable) native listeners always read current values.
    const openRef = useRef(open); openRef.current = open;
    const matchesRef = useRef(matches); matchesRef.current = matches;
    const highlightRef = useRef(highlight); highlightRef.current = highlight;

    const getEditable = () => containerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]') ?? null;

    const close = () => { setOpen(false); triggerLen.current = 0; };

    // Read the text just left of a collapsed caret and open/close the callout on an in-progress token.
    const refresh = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) { close(); return; }
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType !== Node.TEXT_NODE) { close(); return; }
        const before = (node.textContent ?? "").slice(0, range.startOffset);
        const m = before.match(RICH_TRIGGER);
        if (m && suggestions.length > 0) {
            triggerLen.current = m[0].length;
            setQuery(m[1]);
            setHighlight(0);
            const caretRect = range.getBoundingClientRect();
            const boxRect = containerRef.current?.getBoundingClientRect();
            setPoint({
                x: caretRect.left || boxRect?.left || 0,
                y: caretRect.bottom || caretRect.top || boxRect?.top || 0
            });
            setOpen(true);
        } else {
            close();
        }
    };

    const insert = (token: string) => {
        const editable = getEditable();
        const sel = window.getSelection();
        if (!editable || !sel || sel.rangeCount === 0) { close(); return; }
        editable.focus();
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const end = range.startOffset;
        const start = Math.max(0, end - triggerLen.current);
        try {
            // Select the typed trigger so the insert replaces it.
            range.setStart(node, start);
            range.setEnd(node, end);
            sel.removeAllRanges();
            sel.addRange(range);
        } catch { /* stale range — fall through and insert at the current caret */ }
        if (!document.execCommand("insertText", false, token)) {
            range.deleteContents();
            const textNode = document.createTextNode(token);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            editable.dispatchEvent(new InputEvent("input", {bubbles: true}));
        }
        close();
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const onInput = () => refresh();
        // Capture-phase keydown so we intercept nav keys before the editor acts on them while the callout is open.
        const onKeyDown = (e: KeyboardEvent) => {
            if (!openRef.current || matchesRef.current.length === 0) return;
            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault(); e.stopPropagation();
                    setHighlight(h => (h + 1) % matchesRef.current.length);
                    break;
                case "ArrowUp":
                    e.preventDefault(); e.stopPropagation();
                    setHighlight(h => (h - 1 + matchesRef.current.length) % matchesRef.current.length);
                    break;
                case "Enter":
                case "Tab":
                    e.preventDefault(); e.stopPropagation();
                    insert(matchesRef.current[highlightRef.current].token);
                    break;
                case "Escape":
                    e.preventDefault(); e.stopPropagation();
                    close();
                    break;
            }
        };
        container.addEventListener("input", onInput);
        container.addEventListener("mouseup", onInput);
        container.addEventListener("keyup", onInput);
        container.addEventListener("keydown", onKeyDown, true);
        return () => {
            container.removeEventListener("input", onInput);
            container.removeEventListener("mouseup", onInput);
            container.removeEventListener("keyup", onInput);
            container.removeEventListener("keydown", onKeyDown, true);
        };
    }, [containerRef, suggestions]);

    if (!open || matches.length === 0) return null;
    return (
        <Callout target={point} onDismiss={close} isBeakVisible={false} gapSpace={4} setInitialFocus={false}
                 directionalHint={DirectionalHint.bottomLeftEdge}
                 styles={{root: {maxWidth: 460, border: "1px solid #edebe9", borderRadius: 4}}}>
            <TokenSuggestionList matches={matches} highlight={highlight} onHover={setHighlight} onPick={insert} />
        </Callout>
    );
};

export default ExtensionDynamicFieldMap;
